/**
 * The saved copy a CID Forms workspace is working on (Leon, 2026-10-14).
 *
 * Printing a numbered document now saves it on the server under its Ref No.
 * (company_document_issues), so the agent can open it again — from any device —
 * when the vendor or tenant changes something, and print it again under the SAME
 * number. The copy is named in the URL (`/downloads/:id?issue=<id>`), which keeps
 * the routes unchanged and makes Back from the workspace land on the list.
 *
 * After the first print of a new document the returned issue_id is ADOPTED: the
 * URL gains `?issue=` so a second print in the same visit reprints that number
 * instead of taking another. `justAdopted()` tells the page's load effect that the
 * URL changed because of that, not because a different copy was opened — the
 * values on screen are already the copy's.
 *
 * Shared by the phone DocumentWorkspacePage and the desktop DocumentWorkspace.
 */
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as documentsApi from '../api/documents';

export default function useSavedCopy() {
  const [params, setParams] = useSearchParams();
  const issueId = params.get('issue') || null;
  // { id, document_id, title, document_active, doc_ref, print_count, last_printed_at, updated_at } | null
  const [copy, setCopy] = useState(null);
  const adopted = useRef(null);

  /** Fetch the copy the URL names → { values, copy }, or null for a new document. Throws if not found. */
  const load = async () => {
    if (!issueId) { setCopy(null); return null; }
    const r = await documentsApi.getIssuedDocument(issueId);
    const { values, ...meta } = r.data || {};
    setCopy(meta);
    return { values: values && typeof values === 'object' ? values : {}, copy: meta };
  };

  const justAdopted = () => !!issueId && adopted.current === issueId;

  /**
   * After a Print: what became of the copy. 'saved' — kept (a first print is
   * adopted into the URL; `allowNav` lets that URL change past the unsaved-changes
   * guard), 'unsaved' — printed with a number but the save failed, null — a
   * document with no running number, which has nothing to file it under.
   */
  const recordPrint = (data, { allowNav } = {}) => {
    const { issue_id: savedId, doc_ref: ref } = data || {};
    if (!savedId) return ref ? 'unsaved' : null;
    const now = new Date().toISOString();
    setCopy((c) => (c && c.id === savedId
      ? { ...c, print_count: (c.print_count || 0) + 1, last_printed_at: now, updated_at: now }
      : { id: savedId, doc_ref: ref, document_active: true, print_count: 1, last_printed_at: now, updated_at: now }));
    if (savedId !== issueId) {
      adopted.current = savedId;
      allowNav?.();
      setParams((p) => { const n = new URLSearchParams(p); n.set('issue', savedId); return n; }, { replace: true });
    }
    return 'saved';
  };

  return { issueId, copy, load, justAdopted, recordPrint };
}
